import { useEffect, useState, useCallback } from 'react';
import { adminApi } from '@/lib/adminApi';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import {
  Search, Package, TrendingUp, Truck, XCircle, DollarSign, MapPin, MapPinOff, Trash2,
} from 'lucide-react';
import { UserAvatar } from '@/components/UserAvatar';
import { formatDominicaPhone } from '@/lib/formatPhone';

interface PackageEntry {
  id: string;
  driver_id: string;
  driver_name?: string;
  driver_phone?: string | null;
  driver_picture_url?: string | null;
  license_plate?: string | null;
  recipient_name: string;
  recipient_phone?: string | null;
  stop_label: string;
  amount_xcd: number;
  status: string;
  notes?: string | null;
  created_at: string;
  delivered_at: string | null;
  /**
   * A GPS fix was captured when the driver marked the parcel delivered. This is
   * the only part of the record the platform observed rather than being told —
   * everything else, including the delivery itself, is the driver's word.
   */
  location_recorded?: boolean;
  delivered_lat?: number | null;
  delivered_lng?: number | null;
  /**
   * Set when the driver removed the entry. Surfaced rather than filtered: an
   * entry deleted after the fact is exactly what a dispute investigation needs.
   */
  deleted_at?: string | null;
  status_changes?: number;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface Totals {
  pending: number;
  delivered: number;
  cancelled: number;
  /** Deliveries that recorded a location — not deliveries that were verified. */
  located: number;
  deleted: number;
  collected_xcd: number;
  driver_count: number;
}

const statusColors: Record<string, string> = {
  delivered: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  pending: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  cancelled: 'bg-red-500/10 text-red-400 border-red-500/20',
  collected: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
};

export default function Packages() {
  const [entries, setEntries] = useState<PackageEntry[]>([]);
  const [totals, setTotals] = useState<Totals | null>(null);
  const [days, setDays] = useState(30);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  // The RPC takes a UUID, so only a pasted driver id can be pushed server-side.
  // Anything else an operator types is matched against the loaded rows below.
  const driverIdFilter = UUID_RE.test(search.trim()) ? search.trim() : undefined;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await adminApi.getPackages({
        days,
        status: statusFilter === 'all' ? undefined : statusFilter,
        driver_id: driverIdFilter,
      });
      // RPC returns { ok, days, entries[], totals }
      if (result && result.entries) {
        setEntries(result.entries || []);
        setTotals(result.totals || null);
      } else if (Array.isArray(result)) {
        setEntries(result);
        setTotals(null);
      }
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [days, statusFilter, driverIdFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const term = driverIdFilter ? '' : search.trim().toLowerCase();
  const visible = term
    ? entries.filter((p) => [
        p.driver_name,
        p.driver_phone,
        p.license_plate,
        p.recipient_name,
        p.recipient_phone,
        p.stop_label,
        p.notes,
      ].some((field) => field?.toLowerCase().includes(term)))
    : entries;

  const kpiCards = totals ? [
    { label: 'Pending', value: totals.pending, icon: Package, color: 'text-amber-400', bg: 'bg-amber-500/10' },
    { label: 'Delivered', value: totals.delivered, icon: Truck, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
    // Not a trust score. It only says how many deliveries recorded a GPS fix,
    // which is the one thing here nobody had to be believed about.
    {
      label: 'Location recorded',
      value: `${totals.located ?? 0}/${totals.delivered}`,
      icon: MapPin,
      color: 'text-blue-400',
      bg: 'bg-blue-500/10',
    },
    { label: 'Cancelled', value: totals.cancelled, icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/10' },
    { label: 'Collected (XCD)', value: `$${totals.collected_xcd?.toFixed(0) || 0}`, icon: DollarSign, color: 'text-blue-400', bg: 'bg-blue-500/10' },
    { label: 'Active Drivers', value: totals.driver_count, icon: TrendingUp, color: 'text-purple-400', bg: 'bg-purple-500/10' },
  ] : [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Package Ledger</h1>
        <p className="text-sm text-gray-400 mt-1">
          What drivers logged themselves about parcels they carried (read-only).
          This is a record, not proof of delivery &mdash; the sender and recipient
          are not H-Plate users, so nobody outside the driver confirms a handover.
          The strongest evidence here is where the driver&apos;s phone was when
          they marked a parcel delivered.
        </p>
      </div>

      {/* Summary KPIs */}
      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {kpiCards.map((kpi) => {
            const Icon = kpi.icon;
            return (
              <Card key={kpi.label} className="bg-gray-900/50 border-gray-800">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg ${kpi.bg} flex items-center justify-center`}>
                      <Icon className={`w-4 h-4 ${kpi.color}`} />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">{kpi.label}</p>
                      <p className="text-lg font-bold text-white">{kpi.value}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            placeholder="Search driver, recipient, stop..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-gray-900 border-gray-800 text-white placeholder:text-gray-500"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-32 bg-gray-900 border-gray-800 text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-gray-900 border-gray-800">
            <SelectItem value="all" className="text-gray-300 focus:bg-gray-800 focus:text-white">All Status</SelectItem>
            <SelectItem value="pending" className="text-gray-300 focus:bg-gray-800 focus:text-white">Pending</SelectItem>
            <SelectItem value="delivered" className="text-gray-300 focus:bg-gray-800 focus:text-white">Delivered</SelectItem>
            <SelectItem value="cancelled" className="text-gray-300 focus:bg-gray-800 focus:text-white">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className="w-28 bg-gray-900 border-gray-800 text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-gray-900 border-gray-800">
            <SelectItem value="7" className="text-gray-300 focus:bg-gray-800 focus:text-white">7 days</SelectItem>
            <SelectItem value="14" className="text-gray-300 focus:bg-gray-800 focus:text-white">14 days</SelectItem>
            <SelectItem value="30" className="text-gray-300 focus:bg-gray-800 focus:text-white">30 days</SelectItem>
            <SelectItem value="90" className="text-gray-300 focus:bg-gray-800 focus:text-white">90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card className="bg-gray-900/50 border-gray-800">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 bg-gray-800/50 rounded" />
              ))}
            </div>
          ) : visible.length === 0 ? (
            <div className="p-12 text-center">
              <Package className="w-10 h-10 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500">
                {entries.length === 0 ? 'No package entries found' : 'No entries match your search'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-gray-800 hover:bg-transparent">
                  <TableHead className="text-gray-400">Driver</TableHead>
                  <TableHead className="text-gray-400">Recipient</TableHead>
                  <TableHead className="text-gray-400">Drop-off</TableHead>
                  <TableHead className="text-gray-400">Amount</TableHead>
                  <TableHead className="text-gray-400">Status</TableHead>
                  <TableHead className="text-gray-400">Logged</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visible.map((p, idx) => (
                  <TableRow key={p.id || idx} className="border-gray-800 hover:bg-gray-800/30">
                    <TableCell>
                      <div className="flex items-center gap-2.5">
                        <UserAvatar
                          size="sm"
                          name={p.driver_name || null}
                          src={p.driver_picture_url}
                        />
                        <div className="flex flex-col">
                          <span className="text-white text-sm">
                            {p.driver_name || <span className="font-mono text-xs text-gray-400">{p.driver_id?.slice(0, 8)}...</span>}
                          </span>
                          <div className="flex items-center gap-3 mt-0.5">
                            {p.driver_phone && (
                              <a
                                href={`tel:${p.driver_phone.replace(/\s+/g, '')}`}
                                className="font-mono text-xs text-gray-500 hover:underline"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {formatDominicaPhone(p.driver_phone)}
                              </a>
                            )}
                            {p.license_plate && (
                              <span className="font-mono text-xs text-gray-500">{p.license_plate}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="text-gray-200 text-sm">{p.recipient_name}</span>
                        {p.recipient_phone && (
                          <a
                            href={`tel:${p.recipient_phone.replace(/\s+/g, '')}`}
                            className="font-mono text-xs text-gray-500 hover:underline mt-0.5"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {formatDominicaPhone(p.recipient_phone)}
                          </a>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="text-gray-300 text-sm">{p.stop_label}</span>
                        {p.notes && (
                          <span className="text-xs text-gray-500 mt-0.5 max-w-[220px] truncate" title={p.notes}>
                            {p.notes}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-white font-medium">
                      ${Number(p.amount_xcd ?? 0).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col items-start gap-1">
                        <Badge className={statusColors[p.status] || statusColors.pending}>
                          {p.status}
                        </Badge>
                        {p.status === 'delivered' && (
                          p.location_recorded && p.delivered_lat != null ? (
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${p.delivered_lat},${p.delivered_lng}`}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 text-[11px] text-blue-400 hover:text-blue-300"
                              title="Where the driver's phone was when they marked this delivered"
                            >
                              <MapPin className="w-3 h-3" /> marked here
                            </a>
                          ) : (
                            <span
                              className="inline-flex items-center gap-1 text-[11px] text-gray-500"
                              title="No GPS fix was captured when the driver marked this delivered"
                            >
                              <MapPinOff className="w-3 h-3" /> no location
                            </span>
                          )
                        )}
                        {p.deleted_at && (
                          <span
                            className="inline-flex items-center gap-1 text-[11px] text-red-400"
                            title={`Deleted by the driver on ${new Date(p.deleted_at).toLocaleString()}`}
                          >
                            <Trash2 className="w-3 h-3" /> deleted by driver
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-gray-400 text-sm">
                      <div className="flex flex-col">
                        <span>{new Date(p.created_at).toLocaleDateString()}</span>
                        {p.delivered_at && (
                          <span className="text-xs text-gray-500 mt-0.5">
                            Delivered {new Date(p.delivered_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
